import { ILocationTree } from 'legacy/lut/components/shared/locationTree/LocationTree'
import { LocationTreeExtended } from 'legacy/lut/components/shared/locationTree/LocationTreeExtended'
import { IPhoneEditor } from 'legacy/lut/components/widgets/public/phoneEditor/models'
import { isAppLut } from 'legacy/lut/functions'
import { IDropdownProps } from 'legacy/lut/library/dropdown'
import React, { useMemo, useState } from 'react'

import { Button, IButtonProps } from 'core-components/library/button'
import { IInputProps, ITextareaProps } from 'core-components/library/inputs'
import { LoaderOverlay } from 'core-components/library/loader'
import {
  Controller,
  isValidEmail,
  IValidatorProps,
  ValidationStatus,
} from 'core-components/library/validation'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
  getFullPhone,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'
import {
  OnboardingDropdown,
  OnboardingInput,
  OnboardingTextarea,
  OnboardingValidateInput,
} from 'onboarding/components/ui/theme'
import { OnboardingPhoneEditorAdapter } from 'onboarding/components/ui/visualAudienceAdapters'
import { PartnerTerritoryInfo } from 'onboarding/components/widgets/public/forms/partnerTerritoryInfo/PartnerTerritoryInfo'

import { isForwarderEmailValid } from './partnerShip/helpers/isForwarderEmailValid'
import { IPartnerShipLA, usePartnerShipLA } from './partnerShip/hooks/usePartnerShipLA'
import { usePartnerShip } from './usePartnerShip'

import styles from './PartnerShipLA.module.scss'

interface IProps {
  requestId: string
  partnerId: string
  audienceSettings: IAudienceCondition
  disabled?: boolean
}

export const PartnerShipLA = ({ requestId, partnerId, audienceSettings, disabled }: IProps) => {
  const [item, setItem] = useState<IPartnerShipLA>({} as IPartnerShipLA)
  const [canSave, setCanSave] = useState(false)
  const { wip, onSave, partnerTerritory } = usePartnerShipLA((value: IPartnerShipLA) => {
    setItem(value)
  }, requestId)
  const { activeSource } = usePartnerShip()
  const phoneCheckFn = (field: string, value: string) => {
    const label = field.includes('Work') ? 'WorkPhone' : 'MobilePhone'

    if (field === `${label}_Prefix` && !value && item[label]) {
      return 'Incorrect value'
    } else if (field === label && !value && item[`${label}_Prefix`]) {
      return 'Incorrect value'
    }
    return ''
  }

  const onChange = (field: keyof IPartnerShipLA, number?: boolean) => (value: string | number) => {
    if (number) {
      setItem({ ...item, [field]: Number(value) })
    } else {
      setItem({ ...item, [field]: value })
    }
  }

  const chosenTerritory = useMemo(() => {
    return item.PartnerTerritoryISO ? item.PartnerTerritoryISO.split(',') : []
  }, [item.PartnerTerritoryISO])

  const callablePhoneAdditionalAttrs = {
    partnerId,
    contactProcessedInScout: isAppLut(),
  }

  return (
    <Controller
      onValidationStatusChange={({ status }) => {
        if (status !== ValidationStatus.WIP) {
          setCanSave(status === ValidationStatus.Valid)
        }
      }}
    >
      <div className={styles.container}>
        <LoaderOverlay show={wip} />

        <div className={styles.root}>
          <div className={styles.root__firstChild}>
            <PartnerTerritoryInfo />
            <VisualAudienceExtended<ILocationTree>
              write={audienceSettings}
              component={LocationTreeExtended}
              partnerTerritory={partnerTerritory}
              label={'Partner Territory'}
              value={chosenTerritory}
              onChange={(value: string[]) => onChange('PartnerTerritoryISO')(value.join(','))}
              root={'Region-AMS'}
              margin={'md'}
              disabled={disabled}
            />
            <div className={styles.text__title}>Signatory details</div>
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`First name`}
              value={item.FirstName || ''}
              margin={'md'}
              onChange={onChange('FirstName')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`Last name`}
              value={item.LastName || ''}
              margin={'md'}
              onChange={onChange('LastName')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps & IValidatorProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`Email`}
              value={item.Email || ''}
              margin={'md'}
              onChange={onChange('Email')}
              checkFn={isValidEmail}
              disabled={disabled}
            />

            <div className={styles.phoneRow}>
              <CallablePhoneWrapper
                callMode={CALL_MODE.byIcon}
                type={MY_CONNECT_TYPE.Partner}
                phone={getFullPhone(item.WorkPhone_Prefix, item.WorkPhone)}
                additionalAttributes={callablePhoneAdditionalAttrs}
              >
                <VisualAudienceExtended<IPhoneEditor>
                  component={OnboardingPhoneEditorAdapter}
                  write={audienceSettings}
                  checkCodeFn={async (val) => phoneCheckFn('WorkPhone_Prefix', val)}
                  checkPhoneFn={async (val) => phoneCheckFn('WorkPhone', val)}
                  label={'Work Phone'}
                  codeValue={item.WorkPhone_Prefix || ''}
                  phoneValue={item.WorkPhone || ''}
                  onCodeChange={onChange('WorkPhone_Prefix')}
                  onPhoneChange={onChange('WorkPhone')}
                  isDisabled={disabled}
                />
              </CallablePhoneWrapper>
            </div>

            <div className={styles.phoneRow}>
              <CallablePhoneWrapper
                callMode={CALL_MODE.byIcon}
                type={MY_CONNECT_TYPE.Partner}
                phone={getFullPhone(item.MobilePhone_Prefix, item.MobilePhone)}
                additionalAttributes={callablePhoneAdditionalAttrs}
              >
                <VisualAudienceExtended<IPhoneEditor>
                  component={OnboardingPhoneEditorAdapter}
                  write={audienceSettings}
                  checkCodeFn={async (val) => phoneCheckFn('MobilePhone_Prefix', val)}
                  checkPhoneFn={async (val) => phoneCheckFn('MobilePhone', val)}
                  label={'Mobile Phone'}
                  codeValue={item.MobilePhone_Prefix || ''}
                  phoneValue={item.MobilePhone || ''}
                  onCodeChange={onChange('MobilePhone_Prefix')}
                  onPhoneChange={onChange('MobilePhone')}
                  isDisabled={disabled}
                />
              </CallablePhoneWrapper>
            </div>

            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`Job Function`}
              value={item.JobFunction || ''}
              margin={'md'}
              onChange={onChange('JobFunction')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`Job Title`}
              value={item.JobTitle || ''}
              margin={'md'}
              onChange={onChange('JobTitle')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={`Invitation Code`}
              value={item.InvitationCode || ''}
              margin={'md'}
              onChange={onChange('InvitationCode')}
              disabled={disabled}
            />
            <VisualAudienceExtended<ITextareaProps>
              component={OnboardingTextarea}
              write={audienceSettings}
              label={`Invitation Code Comment`}
              value={item.InvitationCodeComment || ''}
              margin={'md'}
              onChange={onChange('InvitationCodeComment')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IDropdownProps>
              component={OnboardingDropdown}
              write={audienceSettings}
              source={activeSource}
              caption={'value'}
              label={`Is user who applied inactive?`}
              value={
                item && item.IsInactiveUser !== undefined
                  ? `${Number(Boolean(item.IsInactiveUser))}`
                  : ''
              }
              margin={'md'}
              onChange={onChange('IsInactiveUser', true)}
              disabled={disabled}
            />

            <div className={styles.btnWrapper}>
              <VisualAudienceExtended<IButtonProps>
                disabled={disabled || !canSave}
                write={audienceSettings}
                component={Button}
                onClick={() => onSave(item)}
              >
                Save
              </VisualAudienceExtended>
            </div>
          </div>
          <div className={styles.root__secondChild}>
            <div className={styles.text__title}>Shipper data</div>
            <div className={styles.text__main}>
              This information will be completed by Partner - {'>'} DO NOT fill in.
            </div>
            <div className={styles.text}>Requested only for HP Company contracts.</div>
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Shipper name'}
              margin={'md'}
              value={item.ShipperName}
              onChange={onChange('ShipperName')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Destination'}
              margin={'md'}
              value={item.Destination}
              onChange={onChange('Destination')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Freight Forwarder name'}
              margin={'md'}
              value={item.ForwarderName}
              onChange={onChange('ForwarderName')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Freight Forwarder address'}
              margin={'md'}
              value={item.ForwarderAddress}
              onChange={onChange('ForwarderAddress')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Freight Forwarder contact'}
              margin={'md'}
              value={item.ForwarderContact}
              onChange={onChange('ForwarderContact')}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps & IValidatorProps>
              component={OnboardingValidateInput}
              write={audienceSettings}
              label={'Freight Forwarder contact email'}
              margin={'md'}
              value={item.ForwarderContactEmail}
              onChange={onChange('ForwarderContactEmail')}
              checkFn={isForwarderEmailValid}
              disabled={disabled}
            />
            <VisualAudienceExtended<IInputProps>
              component={OnboardingInput}
              write={audienceSettings}
              label={'Freight Forwarder phone number'}
              margin={'md'}
              value={item.ForwarderPhoneNumber}
              onChange={onChange('ForwarderPhoneNumber')}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </Controller>
  )
}
